import { useEffect, useState } from "react";
import { useCustom, useTranslate } from "@refinedev/core";
import { Button } from "@fawkes/ui";
import { TriangleAlert } from "lucide-react";

/**
 * "Bestaat deze relatie al?" — de waarschuwing bij het aanmaken.
 *
 * <p><b>Waarom dit bestaat.</b> Bij Leiden was onduidelijk of een relatie er al in stond en zijn er
 * twee aangemaakt (Maarten, 28-10-2026). Aan een relatie hangen dieren, facturen en verblijven; die
 * raken dan verdeeld over twee dossiers die geen van beide compleet zijn, en niemand ziet dat.
 *
 * <p><b>Het blokkeert niet.</b> Twee mensen met dezelfde achternaam op hetzelfde adres bestaan echt,
 * en een gezin deelt soms één mailadres. Daarom staat de reden erbij — "zelfde e-mailadres" vraagt om
 * een andere beslissing dan "zelfde achternaam op dit adres" — en kiest de medewerker: naar die
 * relatie, of toch een nieuwe. Hetzelfde patroon als de chipnummerwaarschuwing bij het inboeken.
 *
 * <p><b>Pas na een korte stilte.</b> De vraag gaat de deur uit zodra er een halve seconde niet is
 * getikt; bij elke toetsaanslag zoeken zou de server laten zoeken op "J", "Ja", "Jan" en een lijst
 * opleveren die nog tijdens het typen verspringt.
 */

export interface DubbelTreffer {
  relatie: {
    id: number;
    klantNummer: number | null;
    klantnaam: string | null;
    organisatie: boolean;
    woonplaats: string | null;
    emailAdres: string | null;
    roles: string[];
  };
  redenen: string[];
}

export interface DubbelVelden {
  achternaam?: string;
  bedrijfsnaam?: string;
  kvkNummer?: string;
  email?: string;
  telefoon?: string;
  postcode?: string;
  huisnummer?: string;
}

/** Wat er minimaal ingevuld moet zijn voordat zoeken zin heeft. */
function heeftGenoeg(v: DubbelVelden): boolean {
  const gevuld = (s?: string) => (s ?? "").trim().length > 0;
  // Naam en adres samen, of één van de harde sporen op zichzelf. Een los ingetikte achternaam is
  // geen spoor: dan zou elke "Jansen" in het bestand bovenkomen.
  if (gevuld(v.email) || gevuld(v.kvkNummer) || gevuld(v.bedrijfsnaam)) return true;
  if ((v.telefoon ?? "").replace(/\D/g, "").length >= 7) return true;
  return gevuld(v.achternaam) && gevuld(v.postcode) && gevuld(v.huisnummer);
}

/** Zoekt de dubbels; geeft de treffers terug zodat het scherm zijn knoppen erop kan aanpassen. */
export function useRelatieDubbels(velden: DubbelVelden, excludeId?: string | number | null): DubbelTreffer[] {
  const [traag, setTraag] = useState<DubbelVelden>({});

  useEffect(() => {
    const t = setTimeout(() => setTraag(velden), 500);
    return () => clearTimeout(t);
    // Op de inhoud en niet op het object: een nieuw object bij elke render zou de teller telkens
    // opnieuw starten en de vraag nooit laten vertrekken.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [velden.achternaam, velden.bedrijfsnaam, velden.kvkNummer, velden.email, velden.telefoon,
      velden.postcode, velden.huisnummer]);

  const zoeken = heeftGenoeg(traag);
  const { data } = useCustom<{ treffers: DubbelTreffer[] }>({
    url: "relaties/dubbel",
    method: "get",
    config: {
      query: {
        achternaam: traag.achternaam ?? "",
        bedrijfsnaam: traag.bedrijfsnaam ?? "",
        kvkNummer: traag.kvkNummer ?? "",
        email: traag.email ?? "",
        telefoon: traag.telefoon ?? "",
        postcode: traag.postcode ?? "",
        huisnummer: traag.huisnummer ?? "",
        ...(excludeId == null ? {} : { exclude: String(excludeId) }),
      },
    },
    queryOptions: { enabled: zoeken },
  });

  if (!zoeken) return [];
  return data?.data?.treffers ?? [];
}

export function RelatieDubbelWaarschuwing({ treffers, onOpen }: {
  treffers: DubbelTreffer[];
  /** Naar de bestaande relatie gaan in plaats van een nieuwe aan te maken. */
  onOpen: (id: number) => void;
}) {
  const t = useTranslate();
  if (treffers.length === 0) return null;

  return (
    <section className="fawkes-dubbel" role="status">
      <p className="fawkes-dubbel__kop">
        <TriangleAlert size={16} aria-hidden="true" />
        {treffers.length === 1
          ? t("relatieDubbel.kopEen")
          : t("relatieDubbel.kop", { args: [String(treffers.length)] })}
      </p>
      <ul className="fawkes-dubbel__lijst">
        {treffers.map((tr) => (
          <li key={tr.relatie.id}>
            <div className="fawkes-dubbel__rij">
              <span className="fawkes-dubbel__naam">{tr.relatie.klantnaam ?? "—"}</span>
              <span className="fawkes-dubbel__sub">
                {[
                  tr.relatie.klantNummer == null ? null : `#${tr.relatie.klantNummer}`,
                  tr.relatie.woonplaats,
                  tr.relatie.emailAdres,
                ].filter(Boolean).join(" · ")}
              </span>
              {/* De reden erbij: waaróm het scherm denkt dat dit dezelfde is, zodat de medewerker
                  dat kan beoordelen in plaats van te moeten raden. */}
              <span className="fawkes-dubbel__redenen">
                {tr.redenen.map((r) => (
                  <span key={r} className="fawkes-dubbel__reden">{t(`relatieDubbel.reden.${r}`)}</span>
                ))}
              </span>
            </div>
            <Button variant="secondary" onClick={() => onOpen(tr.relatie.id)}>
              {t("relatieDubbel.openen")}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
